import { useState } from "react";
import { AlignLeft, Bell, CalendarDays, CalendarClock, Hash, ListTodo, MapPin, Pencil, Repeat, Tag, Trash2, X } from "lucide-react";
import { useDeleteEvent, useEventToResolution, useRestoreEvent } from "../../api/hooks";
import type { CalEvent } from "../../api/types";
import { addDays, datePart, diffDays, type DateStr } from "../../lib/date";
import { draftFromEvent } from "../../lib/draft";
import { anchorOf, useUi, type Anchor } from "../../state/ui";
import { MiniCalendar } from "../MiniCalendar";
import { useMoveEvent } from "../views/common";
import { Popover } from "../ui/Popover";
import { reminderLabel, whenLabel } from "./labels";
import "./overlays.css";

export function useDeleteWithUndo() {
  const ui = useUi();
  const remove = useDeleteEvent();
  const restore = useRestoreEvent();
  return async (event: CalEvent) => {
    const scope = await ui.askScope(event, "delete");
    if (!scope) return;
    ui.setOverlay(null);
    remove.mutate(
      { event, scope },
      {
        onSuccess: ({ undo }) =>
          ui.showToast("Evento excluído", undo ? { label: "Desfazer", run: () => restore.mutate(undo) } : undefined),
        onError: (err) => ui.showToast(`Não foi possível excluir: ${(err as Error).message}`)
      }
    );
  };
}

/** "Virar resolução": out of the agenda, into Resoluções (undated), with an undo. */
export function useToResolutionWithUndo() {
  const ui = useUi();
  const { convert, undo } = useEventToResolution();
  return (event: CalEvent) => {
    ui.setOverlay(null);
    convert.mutate(event, {
      onSuccess: ({ resolution, undo: back }) =>
        ui.showToast(
          `“${resolution.title}” virou resolução`,
          back ? { label: "Desfazer", run: () => undo.mutate({ eventId: back.eventId, resolutionId: resolution.id }) } : undefined
        ),
      onError: (err) => ui.showToast(`Não foi possível transformar: ${(err as Error).message}`)
    });
  };
}

function ToResolution({ event }: { event: CalEvent }) {
  const toResolution = useToResolutionWithUndo();
  return (
    <div className="details-row">
      <ListTodo size={20} className="details-icon" />
      <div className="details-move">
        <div className="details-move-opts">
          <button className="pill" onClick={() => toResolution(event)}>
            Transformar em resolução
          </button>
        </div>
        <span className="details-move-hint">
          {event.isRecurring ? "A rotina toda sai da agenda e vai para Resoluções, sem data." : "Sai da agenda e vai para Resoluções, sem data."}
        </span>
      </div>
    </div>
  );
}

/**
 * Quick reschedule, Google-style: move to another day keeping the time. Shortcuts for
 * the usual cases plus a month picker; series ask which occurrences (via useMoveEvent).
 */
function Reschedule({ event }: { event: CalEvent }) {
  const ui = useUi();
  const moveEvent = useMoveEvent();
  const [picker, setPicker] = useState<Anchor | null>(null);
  const day = datePart(event.start);
  const moveTo = (target: DateStr) => {
    setPicker(null);
    ui.setOverlay(null);
    const delta = diffDays(day, target);
    if (delta) void moveEvent(event, { deltaDays: delta });
  };
  const shortcuts: Array<{ label: string; date: DateStr }> = [
    { label: "Hoje", date: ui.today },
    { label: "Amanhã", date: addDays(ui.today, 1) },
    { label: "+1 semana", date: addDays(day, 7) }
  ].filter((o) => o.date !== day);

  return (
    <div className="details-row">
      <CalendarClock size={20} className="details-icon" />
      <div className="details-move">
        <span className="details-move-label">Remarcar</span>
        <div className="details-move-opts" role="group" aria-label="Remarcar para">
          {shortcuts.map((o) => (
            <button key={o.label} className="pill" onClick={() => moveTo(o.date)}>
              {o.label}
            </button>
          ))}
          <button className="pill" aria-haspopup="dialog" onClick={(e) => setPicker(anchorOf(e.currentTarget))}>
            Outra data…
          </button>
        </div>
      </div>
      {picker && (
        <Popover anchor={picker} placement="below" onClose={() => setPicker(null)} width={264} keepOpen>
          <MiniCalendar compact value={day} today={ui.today} weekStart={ui.me.settings.weekStart} onSelect={moveTo} />
        </Popover>
      )}
    </div>
  );
}

export function EventDetails({ event, anchor }: { event: CalEvent; anchor: Anchor }) {
  const ui = useUi();
  const deleteEvent = useDeleteWithUndo();
  const calendar = ui.calendars.find((c) => c.id === event.calendarId);
  const close = () => ui.setOverlay(null);

  return (
    <Popover anchor={anchor} onClose={close} width={448} className="details">
      <div className="details-actions">
        <button className="icon-btn" aria-label="Editar evento" title="Editar evento (e)" onClick={() => ui.setOverlay({ kind: "editor", draft: draftFromEvent(event), event })}>
          <Pencil size={18} />
        </button>
        <button className="icon-btn" aria-label="Excluir evento" title="Excluir evento (Delete)" onClick={() => void deleteEvent(event)}>
          <Trash2 size={18} />
        </button>
        <button className="icon-btn" aria-label="Fechar" onClick={close}>
          <X size={20} />
        </button>
      </div>
      <div className="details-body">
        <div className="details-row">
          <span className="details-swatch" style={{ background: event.color }} />
          <div>
            <h2 className="details-title">{event.title || "(sem título)"}</h2>
            <div className="details-when">{whenLabel(event)}</div>
            {event.recurrenceText && (
              <div className="details-when">
                <Repeat size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
                {event.recurrenceText.charAt(0).toUpperCase() + event.recurrenceText.slice(1)}
              </div>
            )}
          </div>
        </div>
        <Reschedule event={event} />
        <ToResolution event={event} />
        {event.location && (
          <div className="details-row">
            <MapPin size={20} className="details-icon" />
            <div>{event.location}</div>
          </div>
        )}
        {event.description && (
          <div className="details-row">
            <AlignLeft size={20} className="details-icon" />
            <div className="details-desc">{event.description}</div>
          </div>
        )}
        {event.reminders.length > 0 && (
          <div className="details-row">
            <Bell size={20} className="details-icon" />
            <div>
              {event.reminders.map((m) => (
                <div key={m}>{reminderLabel(m, event.allDay)}</div>
              ))}
            </div>
          </div>
        )}
        {calendar && (
          <div className="details-row">
            <CalendarDays size={20} className="details-icon" />
            <div>{calendar.name}</div>
          </div>
        )}
        {event.categoryLabel && event.category !== "outros" && (
          <div className="details-row">
            <Tag size={20} className="details-icon" />
            <div>{event.categoryLabel}</div>
          </div>
        )}
        <div className="details-row subtle">
          <Hash size={20} className="details-icon" />
          <div>
            Código <strong>#{event.code}</strong> — use no WhatsApp: “muda o #{event.code} para…”
          </div>
        </div>
      </div>
    </Popover>
  );
}
